import { useEffect, useState, useCallback } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtL, fmtDate, todayStr } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/DateInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Scale, Loader2, Save, Trash2, Lock, AlertTriangle, CheckCircle2, TrendingUp } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const inp = "mt-1.5 bg-[#0B0F17] border-[#374151] text-white";
const SHIFTS = ["Morning", "Afternoon", "Night"];

export default function ShiftReconciliation() {
  const { user } = useAuth();
  const { stationId, setStationId: setStation, stations } = useStation();
  const station = stationId === "all" ? (stations[0]?.id || "") : stationId;
  const [date, setDate] = useState(todayStr());
  const [shift, setShift] = useState("Morning");
  const [pumps, setPumps] = useState([]);
  const [readings, setReadings] = useState([]); // for selected date+shift (entry)
  const [report, setReport] = useState(null);   // whole day report
  const [drafts, setDrafts] = useState({});      // pump_id -> {opening, closing, pos}
  const [savingId, setSavingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [trendMonth, setTrendMonth] = useState(new Date().toISOString().slice(0, 7));
  const [trend, setTrend] = useState(null);

  useEffect(() => {
  }, []);

  useEffect(() => {
    if (!station) return;
    api.get(`/stations/${station}/pumps`).then((r) => setPumps(r.data)).catch(() => setPumps([]));
  }, [station]);

  const loadEntry = useCallback(() => {
    if (!station) return;
    api.get(`/shift-readings?station_id=${station}&date=${date}&shift=${shift}`)
      .then((r) => {
        setReadings(r.data);
        const d = {};
        r.data.forEach((x) => { d[x.pump_id] = { opening: x.opening, closing: x.closing, pos: x.pos_liters }; });
        setDrafts(d);
      }).catch(() => setReadings([]));
  }, [station, date, shift]);

  const loadReport = useCallback(() => {
    if (!station) return;
    setLoading(true);
    api.get(`/shift-variance-report?station_id=${station}&date=${date}`)
      .then((r) => setReport(r.data)).catch(() => setReport(null)).finally(() => setLoading(false));
  }, [station, date]);

  useEffect(() => { loadEntry(); loadReport(); }, [loadEntry, loadReport]);

  const loadTrend = useCallback(() => {
    if (!station) return;
    api.get(`/shift-fault-trend?station_id=${station}&month=${trendMonth}`)
      .then((r) => setTrend(r.data)).catch(() => setTrend(null));
  }, [station, trendMonth]);
  useEffect(() => { loadTrend(); }, [loadTrend]);

  const existingFor = (pumpId) => readings.find((r) => r.pump_id === pumpId);
  const setDraft = (pumpId, field, val) => setDrafts((d) => ({ ...d, [pumpId]: { ...(d[pumpId] || {}), [field]: val } }));

  const save = async (pump) => {
    const dr = drafts[pump.id] || {};
    const existing = existingFor(pump.id);
    setSavingId(pump.id);
    try {
      const payload = {
        station_id: station, date, shift, pump_id: pump.id,
        opening: Number(dr.opening || 0), closing: Number(dr.closing || 0), pos_liters: Number(dr.pos || 0),
      };
      if (existing) await api.patch(`/shift-readings/${existing.id}`, payload);
      else await api.post("/shift-readings", payload);
      toast.success(`${pump.label} saved`);
      loadEntry(); loadReport(); loadTrend();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSavingId("");
  };

  const del = async (id) => {
    try { await api.delete(`/shift-readings/${id}`); toast.success("Reading removed"); loadEntry(); loadReport(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const rows = report?.rows || [];
  const t = report?.totals;
  const pumpNoById = Object.fromEntries(pumps.map((p) => [p.id, p.pump_no]));

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Meter vs POS</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <Scale className="w-8 h-8 text-emerald-400" /> Shift Reconciliation
          </h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Track mechanical pump meters against POS sales per shift. Independent of cash-in-hand &amp; P&amp;L — high variance only flags for review.</p>
        </div>
      </div>

      <div className={`${card} p-4 flex flex-wrap gap-3 items-end`} data-testid="shift-filters">
        <div>
          <Label className="text-[#9CA3AF] text-xs">Station</Label>
          <Select value={station} onValueChange={setStation}>
            <SelectTrigger data-testid="shift-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-48"><SelectValue placeholder="Station" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Date</Label>
          <DateInput data-testid="shift-date-input" value={date} onChange={setDate} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Shift</Label>
          <Select value={shift} onValueChange={setShift}>
            <SelectTrigger data-testid="shift-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-36"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">{SHIFTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* ENTRY */}
      <div className={`${card} p-5 sm:p-6`}>
        <h3 className="font-[Outfit] font-semibold text-lg mb-1">Closing meter &amp; POS entry · {shift}</h3>
        <p className="text-xs text-[#9CA3AF] mb-4">Enter the pump totalizer (opening/closing) and the POS litres sold for this shift. Variance = Meter − POS.</p>
        {pumps.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="shift-entry-table">
              <thead>
                <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                  {["Pump", "Opening", "Closing", "Meter L", "POS L", "Variance", ""].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {pumps.map((p) => {
                  const ex = existingFor(p.id);
                  const dr = drafts[p.id] || {};
                  const locked = ex && ex.editable === false;
                  const meter = Math.max(0, Number(dr.closing || 0) - Number(dr.opening || 0));
                  return (
                    <tr key={p.id} className="border-b border-[#1F2937]/60" data-testid={`shift-entry-${p.id}`}>
                      <td className="py-2 pr-3 text-white">
                        {p.pump_no && <span className="block text-[11px] text-[#9CA3AF] font-mono uppercase tracking-wide">Pump {p.pump_no}</span>}
                        {p.label} <span className="text-[#9CA3AF] text-xs">· {p.product}</span>
                      </td>
                      <td className="py-2 pr-3"><Input type="number" step="0.01" disabled={locked} data-testid={`shift-open-${p.id}`} value={dr.opening ?? ""} onChange={(e) => setDraft(p.id, "opening", e.target.value)} className={`${inp} w-28`} /></td>
                      <td className="py-2 pr-3"><Input type="number" step="0.01" disabled={locked} data-testid={`shift-close-${p.id}`} value={dr.closing ?? ""} onChange={(e) => setDraft(p.id, "closing", e.target.value)} className={`${inp} w-28`} /></td>
                      <td className="py-2 pr-3 metric-value text-white/80">{fmtL(meter)}</td>
                      <td className="py-2 pr-3"><Input type="number" step="0.01" disabled={locked} data-testid={`shift-pos-${p.id}`} value={dr.pos ?? ""} onChange={(e) => setDraft(p.id, "pos", e.target.value)} className={`${inp} w-28`} /></td>
                      <td className="py-2 pr-3 metric-value">{ex ? <VarianceBadge v={ex.variance_liters} pct={ex.variance_pct} flagged={ex.flagged} /> : <span className="text-[#4B5563]">—</span>}</td>
                      <td className="py-2 pr-2 text-right whitespace-nowrap">
                        {locked ? (
                          <span className="text-[#9CA3AF] text-xs inline-flex items-center gap-1" title="Locked after 24h — ask a manager"><Lock className="w-3.5 h-3.5" /> Locked</span>
                        ) : (
                          <>
                            <Button size="sm" onClick={() => save(p)} disabled={savingId === p.id} data-testid={`shift-save-${p.id}`} className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-8 px-3 mr-2">
                              {savingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            </Button>
                            {ex && <button onClick={() => del(ex.id)} data-testid={`shift-delete-${p.id}`} className="text-[#9CA3AF] hover:text-red-400"><Trash2 className="w-4 h-4 inline" /></button>}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <div className="text-sm text-[#9CA3AF] py-8 text-center">No pumps at this station. Add pumps first.</div>}
      </div>

      {/* REPORT */}
      <div className={`${card} p-5 sm:p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-[Outfit] font-semibold text-lg">Daily Variance Report · {fmtDate(date)}</h3>
          {t && t.flagged_count > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-red-500/15 text-red-400 border border-red-500/30" data-testid="shift-flag-count">
              <AlertTriangle className="w-4 h-4" /> {t.flagged_count} pump{t.flagged_count === 1 ? "" : "s"} {t.flagged_count === 1 ? "needs" : "need"} Manager Review
            </span>
          )}
        </div>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>
          : rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="shift-report-table">
                <thead>
                  <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                    {["Shift", "Pump", "Product", "Meter L (A)", "POS L (B)", "Variance (C)", "Var %", "Status"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`border-b border-[#1F2937]/60 ${r.flagged ? "bg-red-500/[0.06]" : ""}`} data-testid={`shift-report-row-${r.id}`}>
                      <td className="py-2 pr-3">{r.shift}</td>
                      <td className="py-2 pr-3 text-white">
                        {pumpNoById[r.pump_id] && <span className="block text-[11px] text-[#9CA3AF] font-mono uppercase tracking-wide">Pump {pumpNoById[r.pump_id]}</span>}
                        {r.pump_label}
                      </td>
                      <td className="py-2 pr-3">{r.product}</td>
                      <td className="py-2 pr-3 metric-value text-white/80">{fmtL(r.meter_liters)}</td>
                      <td className="py-2 pr-3 metric-value text-white/80">{fmtL(r.pos_liters)}</td>
                      <td className="py-2 pr-3 metric-value"><VarianceBadge v={r.variance_liters} pct={r.variance_pct} flagged={r.flagged} /></td>
                      <td className="py-2 pr-3 metric-value">{r.variance_pct}%</td>
                      <td className="py-2 pr-3">
                        {r.flagged
                          ? <span className="inline-flex items-center gap-1 text-red-400 font-semibold" data-testid={`shift-flag-${r.id}`}><AlertTriangle className="w-4 h-4" /> Manager Review</span>
                          : <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-4 h-4" /> OK</span>}
                      </td>
                    </tr>
                  ))}
                  {t && (
                    <tr className="border-t-2 border-[#1F2937] font-bold" data-testid="shift-report-total">
                      <td className="py-2.5 pr-3" colSpan={3}>TOTAL</td>
                      <td className="py-2.5 pr-3 metric-value text-white">{fmtL(t.meter_liters)}</td>
                      <td className="py-2.5 pr-3 metric-value text-white">{fmtL(t.pos_liters)}</td>
                      <td className="py-2.5 pr-3 metric-value"><VarianceBadge v={t.variance_liters} pct={t.variance_pct} flagged={false} /></td>
                      <td className="py-2.5 pr-3 metric-value">{t.variance_pct}%</td>
                      <td></td>
                    </tr>
                  )}
                </tbody>
              </table>
              <p className="text-xs text-[#9CA3AF] mt-3">Master record for accounting remains the POS cash total — this report is for pump-fault detection only. Entries lock 24h after entry (managers can still correct).</p>
            </div>
          ) : <div className="text-sm text-[#9CA3AF] py-10 text-center" data-testid="shift-report-empty">No shift readings recorded for this date yet.</div>}
      </div>

      {/* FAULTY-PUMP TREND */}
      <div className={`${card} p-5 sm:p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-[Outfit] font-semibold text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400" /> Faulty-Pump Trend</h3>
            <p className="text-xs text-[#9CA3AF] mt-0.5">Which pumps get flagged most this month — spot a failing meter early.</p>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Month</Label>
            <Input type="month" data-testid="shift-trend-month" value={trendMonth} onChange={(e) => setTrendMonth(e.target.value)} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
          </div>
        </div>
        {trend && trend.pumps.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="shift-trend-table">
              <thead>
                <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                  {["Pump", "Product", "Flags", "Readings", "Flag rate", "Avg |var| %", "Worst var %"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {trend.pumps.map((p, i) => (
                  <tr key={p.pump_id} className={`border-b border-[#1F2937]/60 ${p.flags > 0 && i === 0 ? "bg-red-500/[0.08]" : ""}`} data-testid={`shift-trend-row-${p.pump_id}`}>
                    <td className="py-2 pr-3 text-white font-medium">
                      {pumpNoById[p.pump_id] && <span className="block text-[11px] text-[#9CA3AF] font-mono uppercase tracking-wide">Pump {pumpNoById[p.pump_id]}</span>}
                      {p.flags > 0 && i === 0 && <span className="text-red-400 mr-1" title="Top offender this month">⚠</span>}{p.pump_label}
                    </td>
                    <td className="py-2 pr-3">{p.product}</td>
                    <td className={`py-2 pr-3 metric-value font-semibold ${p.flags > 0 ? "text-red-400" : "text-emerald-400"}`} data-testid={`shift-trend-flags-${p.pump_id}`}>{p.flags}</td>
                    <td className="py-2 pr-3 metric-value text-white/70">{p.readings}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-[#1F2937] overflow-hidden">
                          <div className={`h-full ${p.flag_rate > 0 ? "bg-red-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, p.flag_rate)}%` }} />
                        </div>
                        <span className="metric-value text-white/70 text-xs">{p.flag_rate}%</span>
                      </div>
                    </td>
                    <td className="py-2 pr-3 metric-value text-white/70">{p.avg_pct}%</td>
                    <td className={`py-2 pr-3 metric-value ${p.worst_pct > 2 ? "text-red-400" : "text-white/70"}`}>{p.worst_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-[#9CA3AF] mt-3">A consistently high flag rate on one pump usually means a faulty meter or calibration drift — worth a physical check.</p>
          </div>
        ) : <div className="text-sm text-[#9CA3AF] py-10 text-center" data-testid="shift-trend-empty">No shift data for this month yet.</div>}
      </div>
    </div>
  );
}

function VarianceBadge({ v, flagged }) {
  const cls = flagged ? "text-red-400" : v === 0 ? "text-[#9CA3AF]" : v < 0 ? "text-amber-400" : "text-emerald-400";
  return <span className={cls}>{v > 0 ? "+" : ""}{fmtL(v)}</span>;
}
